import axios from 'axios';
import {EnvConfig} from '../config/envConfig.interface';

type ContextTranslationValue = {[context: string]: string};

export type Translations = {
  [s: string]: string | ContextTranslationValue;
};

export interface ITranslateService {
  initialize(defaultLanguage: string): Promise<void>;
  getByKey(
    keyPath: string,
    fallbackText?: string,
    overrideLanguage?: string,
  ): Promise<string>;
}

export class TranslateService implements ITranslateService {
  private defaultLanguage = '';
  private translations: {[langKey: string]: Translations} = {};

  constructor(private configuration: EnvConfig) {}

  public async initialize(defaultLanguage: string): Promise<void> {
    this.defaultLanguage = defaultLanguage;
    await this.getTranslations(this.defaultLanguage);
  }

  private async getTranslations(lang: string): Promise<Translations> {
    if (this.translations[lang]) {
      return this.translations[lang];
    }

    let fileJson = null;

    try {
      const base = `${this.configuration.embedHostingBaseUrl}/translations`;
      const res = await axios.get(`${base}/${lang}.json`);

      fileJson = res.data;
    } catch (err) {
      console.error('err loading translation', err);
    }

    if (fileJson === null) {
      throw new Error('No translation file found');
    }

    return this.parseTranslations(lang, fileJson);
  }

  private parseTranslations(
    lang: string,
    fileJson: Record<string, {context: string; translation: string}>,
  ): Translations {
    const result: Translations = {};

    for (const [key, translationInfo] of Object.entries(fileJson)) {
      if (translationInfo['context']) {
        const context = translationInfo.context;
        if (!result[context]) {
          result[context] = {
            [key]: translationInfo.translation,
          };
        }

        (result[context] as ContextTranslationValue)[key] =
          translationInfo.translation;
      } else {
        result[key] = translationInfo.translation;
      }
    }

    const resultTyped = result as Translations;

    this.translations[lang] = resultTyped;
    return resultTyped;
  }

  public async getByKey(
    keyPath: string,
    fallbackText?: string,
    overrideLanguage?: string,
  ): Promise<string> {
    const lang = overrideLanguage ?? this.defaultLanguage;
    const translations = await this.getTranslations(lang);

    const pathKeys = keyPath.split('.');
    const contextOrKey = pathKeys[0];
    const key = pathKeys[1];

    const translation = key
      ? (translations[contextOrKey] as ContextTranslationValue)[key]
      : (translations[key] as string);
    if (!translation) {
      if (fallbackText) {
        return fallbackText;
      }
      return key;
    }
    return translation;
  }
}
